// articles/BestBallGame.js
// Game guide (Oct 4, 2026), in the Nassau guide's format: Best Ball for a weekly round, with
// the event steps (registration, shotgun, printing, TV board) left to the Best Ball event
// guide. Settings and labels follow that guide and App.js StrokePlayGamePanel (bestBall);
// change them here if they change there.
import React from 'react';
import { colors, getGuideBySlug } from '../guidesData';
import { H2, H3, P, UL, LI, GuideCTA, FAQ } from '../articleUI';
import { Step, Btn, Heads, StepTracker, PRINT_CSS } from './eventGuideParts';

const guide = getGuideBySlug('how-to-set-up-a-best-ball-game');

const FAQS = [
  {
    q: 'We want a 2 vs 2 match inside our foursome. Is Best Ball the right game?',
    a: 'Best Ball ranks teams on their best-ball total, like stroke play for teams. For a head-to-head match with front, back and overall bets, play a Nassau with 2 vs 2 best ball instead. You can run both off the same cards.',
  },
  {
    q: 'What handicap does each player get?',
    a: 'There is no team handicap. Each player plays off a percentage of his own course handicap from his own tee: the USGA recommendations are 85% for 2-man best ball and 75%, 85% or 100% for best 1, 2 or 3 of 4. 3-man has no USGA figure; TTO starts it at 75% for best 1 of 3 and 85% for best 2 of 3. You can change the percentage.',
  },
  {
    q: 'What happens if a foursome is a player short?',
    a: 'In a 4-man format TTO draws a blind automatically when the round starts: a player picked at random from the missing player\u2019s handicap band, whose scores fill the empty spot. A 2-man or 3-man team is never given a blind.',
  },
  {
    q: 'I changed the pairings. Do the teams update?',
    a: 'Open the game and save again before the first score is entered, so the teams refresh. Once scoring starts the teams are fixed.',
  },
  {
    q: 'Do Best Ball rounds post to GHIN?',
    a: 'Yes. Each player plays his own ball, so his round is acceptable for handicap purposes.',
  },
];

export default function BestBallGame() {
  return (
    <article>
      <h1
        style={{
          fontFamily: "'DM Serif Display', Georgia, serif",
          fontSize: 38,
          fontWeight: 400,
          color: colors.gray900,
          margin: '0 0 8px',
          lineHeight: 1.15,
        }}
      >
        How to Set Up a Best Ball Game
      </h1>
      <style>{PRINT_CSS}</style>
      <div
        style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap', margin: '0 0 28px' }}
      >
        <p style={{ fontSize: 15, color: colors.gray400, margin: 0 }}>
          {guide.readMinutes} min read &middot; Game guide
        </p>
        <button
          type="button"
          className="guide-no-print"
          onClick={() => window.print()}
          style={{
            background: 'white',
            color: colors.green800,
            border: `1px solid ${colors.gray300}`,
            borderRadius: 8,
            padding: '8px 14px',
            fontSize: 14,
            fontWeight: 700,
            cursor: 'pointer',
          }}
        >
          🖨️ Print this guide
        </button>
      </div>

      <P>
        Everyone plays his own ball, and the team counts its best score on each hole (or its best 2
        or 3 in a foursome). Teams are ranked on their total, net or gross, and TTO pays the places
        you choose, with teammates splitting their team&rsquo;s winnings evenly. The teams are your
        pairing groups.
      </P>

      <StepTracker
        storageKey="tto_guide_bestball_game_steps"
        steps={['Teams on the Pairings page', 'Add Best Ball', 'Play & follow the teams', 'Results & settle']}
        note="Steps 1 and 2 happen before the round; 3 and 4 on the day. Tap a step to jump to it."
      />

      <H2>Before you start</H2>
      <UL>
        <LI>
          <strong>Best Ball needs live scoring.</strong> Every player in it needs his scores entered in
          the app as he plays, on his own phone or by the player keeping his group&rsquo;s card.
        </LI>
        <LI>
          <strong>Best Ball isn&rsquo;t in your games list?</strong> Games come with the Plus plan and
          above. On Free or Starter, upgrade under Settings &rarr; Subscription &amp; Billing.
        </LI>
        <LI>
          Decide with your group: 2-man teams, threesomes (best 1 or 2 of 3) or foursomes (best 1,
          2 or 3 of 4); net, gross or
          both; the buy-in; and how many places you pay.
        </LI>
      </UL>

      <H2>Setup, step by step</H2>

      <Step n={1} title="Make the teams on the Pairings page" when="Once you know who's playing">
        <Heads>
          <strong>Each saved pairing group is a team.</strong> The game finds its teams there, and
          nothing counts until pairings are saved.
        </Heads>
        <UL>
          <LI>
            <strong>2-man best ball:</strong> pair the players as twosomes. Two twosomes can play
            together as a foursome on the course; each twosome is its own team.
          </LI>
          <LI>
            <strong>Best 1 or 2 of 3:</strong> pair the players as threesomes. Each threesome is a
            team, and no blind is drawn.
          </LI>
          <LI>
            <strong>Best 1, 2 or 3 of 4:</strong> pair the players as foursomes. Each foursome is a
            team.
          </LI>
          <LI>
            <Btn>⚖️ Balanced Flights ▾</Btn> &rarr; Balanced Twosomes or Balanced Foursomes spreads
            the handicaps so each team has a fair mix, or move players into teams by hand.
          </LI>
          <LI>
            Click <Btn>💾 Save Pairings</Btn>.
          </LI>
        </UL>
      </Step>

      <Step n={2} title="Add Best Ball" when="After pairings are saved">
        <Heads>
          <strong>On a net game, every player needs a complete profile.</strong> Each
          player&rsquo;s profile must include a <strong>preferred tee</strong> and a{' '}
          <strong>Handicap Index</strong>, so that his course handicap can be worked out. Check them
          in Admin &rarr; Players.
        </Heads>
        <UL>
          <LI>
            Click <Btn>🎮 Games</Btn> on the tee time card and tick <strong>Best Ball</strong>.
          </LI>
          <LI>
            <strong>Format:</strong> <Btn>2-man best ball</Btn>, <Btn>Best 1 of 3</Btn>,{' '}
            <Btn>Best 2 of 3</Btn>, <Btn>Best 1 of 4</Btn>, <Btn>Best 2 of 4</Btn>,{' '}
            <Btn>Best 3 of 4</Btn> or <Btn>One gross + one net</Btn>. If the format doesn&rsquo;t
            match your group size (two threesomes on a 4-man format, say), the Games card warns you.
          </LI>
          <LI>
            <strong>One gross + one net</strong> (foursomes): on each hole the team counts one
            player's gross score plus a <em>different</em> player's net score, the pair with the
            lowest total. TTO picks the pair. The net ball starts at 100% of course handicap.
          </LI>
          <LI>
            <strong>Rank on:</strong> Net, Gross or Both. (One gross + one net has a single team
            score, so it skips this.)
          </LI>
          <LI>
            <strong>Handicap allowance:</strong> filled in from the USGA recommendations for the
            format (2-man 85%; best 1, 2 or 3 of 4: 75%, 85%, 100%; best 1 or 2 of 3, which has no
            USGA figure: 75%, 85%). Each player plays off that
            percentage of his own course handicap; <strong>Reset</strong> puts it back.
          </LI>
          <LI>
            <strong>Payouts:</strong>
            <UL>
              <LI>
                <strong>Winner takes all:</strong> enter the <strong>Buy-in</strong> per player; every
                buy-in goes to first place, split on a tie.
              </LI>
              <LI>
                <strong>Paid places:</strong> enter the <strong>Buy-in</strong>, choose{' '}
                <strong>Places paid</strong>, and set each place&rsquo;s percentage (for example
                60 / 40). They must add up to 100%.
              </LI>
              <LI>
                <strong>Handled outside TTO:</strong> TTO names the places and you settle the money
                yourself. Set <strong>Places decided</strong> to the number of places you award.
              </LI>
            </UL>
            Every player pays the buy-in, and teammates split their team&rsquo;s winnings evenly. TTO pays out the whole buy-in; there&rsquo;s no fee kept back.
          </LI>
          <LI>
            <strong>What it pays:</strong> <Btn>Whole round</Btn>, <Btn>Front &amp; back</Btn> or{' '}
            <Btn>Front, back &amp; overall</Btn>. With the nines paid separately, each part is its own
            competition with its own share of the buy-in and its own places, and the standings get
            Front 9 / Back 9 / Overall tabs. It needs an 18-hole round ranked on Net or Gross (not
            Both).
          </LI>
          <LI>
            <strong>Money on a tie:</strong> <strong>Tied scores share the money</strong> (tied
            players split the money for the places they occupy), or <strong>Pay by the decided
            order</strong> (the tie-break below decides the places).
          </LI>
          <LI>
            <strong>How ties are decided:</strong> <strong>Scorecard playoff (USGA)</strong> (best
            score over the last 9, then 6, then 3, then the 18th), <strong>Hole handicap</strong>{' '}
            (hardest hole first), or <strong>We&rsquo;ll decide</strong> (you put them in order).
          </LI>
          <LI>
            Click <Btn>Save Games</Btn>.
          </LI>
        </UL>
        <P>
          After you save, <strong>Teams — from Pairings</strong> lists each team with its players{' '}
          <strong>✓ In</strong> / <strong>— Out</strong>, and any team that is short or has too many
          players. Everyone on a saved team is in; click a player to take him out.
        </P>
      </Step>

      <H2>On the day</H2>

      <Step n={3} title="Play and follow the teams" when="During the round">
        <UL>
          <LI>
            Each player enters his own score on every hole, or one player keeps the card for his
            group with <strong>+ Score for another player</strong>.
          </LI>
          <LI>
            <strong>Picked up?</strong> Tap <strong>X</strong>. It counts as a net double bogey. In a
            4-man format, remind players to hole out: any player&rsquo;s score may count.
          </LI>
          <LI>
            <strong>A foursome a player short</strong> (4-man formats): TTO draws a blind when the
            first score is entered, a player from the field whose scores fill the empty spot.
          </LI>
          <LI>
            Players follow the teams in their app: <strong>🏆 Leaderboard</strong> &rarr;{' '}
            <strong>💰 Games</strong>, one row per team. A hole counts for a team once every player
            on it has scored the hole.
          </LI>
        </UL>
      </Step>

      <Step n={4} title="Results and settling up" when="When the last card is in">
        <UL>
          <LI>
            The round finalizes itself once every card is complete. You can also click{' '}
            <Btn>🏁 Finalize Round &amp; Save Settlements</Btn> in <Btn>🎮 Games</Btn>. If a card is
            incomplete, TTO lists the player and lets you mark him DNF or wait for his scores.
          </LI>
          <LI>
            <strong>Ties for a paid place</strong> are broken by the method you chose. Identical
            cards, or <strong>We&rsquo;ll decide</strong>, need your call: in <Btn>🎮 Games</Btn>, put
            the tied players in order with ▲ ▼ and click <Btn>Use this order</Btn>.
          </LI>
          <LI>
            Teammates split their team&rsquo;s winnings evenly, and each player settles his share.
          </LI>
          <LI>
            With a buy-in, TTO settles the money player to player, every player gets a statement with
            the placings and his net scorecard, and you can email everyone their results with one
            click.
          </LI>
          <LI>
            <strong>A score needs fixing after finalize?</strong> Click <Btn>↶ Reopen Round</Btn> in{' '}
            <Btn>🎮 Games</Btn>, correct it in Admin &rarr; <strong>⛳ Scoring</strong> &rarr;{' '}
            <strong>⛳ Post-Round Score Entry</strong>, then finalize again.
          </LI>
        </UL>
      </Step>

      <H2>Running Best Ball as an event?</H2>
      <P>
        For a tournament (registration, starting holes, printed scorecards and the clubhouse
        leaderboard), use the event guide instead: 📖 Guides &rarr; Setting up Events / Tournaments
        &rarr; <strong>Best Ball</strong>. The game settings are the same as step 1 here.
      </P>
      <H2>What changes once scoring starts</H2>
      <UL>
        <LI>
          <strong>The teams are fixed</strong> once the first score is entered; don&rsquo;t re-pair
          mid-round.
        </LI>
        <LI>
          <strong>Settings can still change</strong>, but change them only if you must: the standings
          and any money follow the new settings.
        </LI>
      </UL>

      <FAQ id="bestball-game" faqs={FAQS} />

      <div className="guide-no-print">
        <GuideCTA />
      </div>
    </article>
  );
}
